<template>
  <div class="voice-reader-controls">
    <!-- Loading state: waiting for first audio clip -->
    <template v-if="isLoading">
      <i
        class="pi pi-spin pi-spinner voice-reader-spinner"
        title="Loading audio..."
      />
      <span class="voice-loading-msg">Preparing your audio, just a moment…</span>
    </template>

    <!-- Weekly limit reached — disabled listen button -->
    <button
      v-else-if="weeklyLimitReached"
      class="voice-reader-btn voice-reader-btn--play voice-reader-btn--limit"
      disabled
      title="Weekly reading limit reached"
    >
      <i class="pi pi-lock" />
      <span class="play-label">Limit Reached</span>
    </button>

    <!-- Listen button (no active session) -->
    <button
      v-else-if="!isReading"
      class="voice-reader-btn voice-reader-btn--play"
      data-test="listen-btn"
      title="Read aloud"
      @click="$emit('play')"
    >
      <i class="pi pi-play" />
      <span class="play-label">Listen</span>
    </button>

    <!-- Resume button (session paused) -->
    <button
      v-else-if="isPaused"
      class="voice-reader-btn voice-reader-btn--play"
      data-test="listen-btn"
      title="Resume"
      @click="$emit('resume')"
    >
      <i class="pi pi-play" />
      <span class="play-label">Resume</span>
    </button>

    <!-- Pause button (currently playing) -->
    <button
      v-else-if="isPlaying"
      class="voice-reader-btn"
      title="Pause"
      @click="$emit('pause')"
    >
      <i class="pi pi-pause" />
    </button>

    <!-- Stop button — only while audio is playing -->
    <button
      v-if="isPlaying"
      class="voice-reader-btn"
      title="Stop"
      @click="$emit('stop')"
    >
      <i class="pi pi-stop" />
    </button>

    <!-- Aurora waveform pulse — visible while playing -->
    <div
      v-if="isPlaying"
      class="voice-waveform"
      aria-hidden="true"
    >
      <span class="wave-bar" />
      <span class="wave-bar" />
      <span class="wave-bar" />
      <span class="wave-bar" />
      <span class="wave-bar" />
    </div>

    <!-- Error message -->
    <span
      v-if="error"
      class="voice-reader-error"
    >
      {{ error }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'VoiceReaderControls',

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    isPlaying: {
      type: Boolean,
      default: false,
    },
    isReading: {
      type: Boolean,
      default: false,
    },
    isPaused: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    weeklyLimitReached: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['play', 'pause', 'resume', 'stop'],
};
</script>

<style scoped>
.voice-reader-controls {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.voice-reader-spinner {
  font-size: 1.1rem;
  color: var(--color-primary-mid);
  margin: 0 6px;
}

.voice-loading-msg {
  font-size: 0.8rem;
  color: var(--color-primary-mid);
  font-family: var(--font-family);
  font-style: italic;
  opacity: 0.85;
}

/* ── Shared base for pause / stop ── */
.voice-reader-btn {
  background: none;
  border: 1.5px solid var(--color-primary);
  cursor: pointer;
  padding: 5px 14px;
  border-radius: var(--radius-md);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  transition: background-color var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);
}

.voice-reader-btn:hover {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.voice-reader-btn .pi {
  font-size: 0.95rem;
}

/* ── Play button — Aurora AI rectangular ── */
.voice-reader-btn--play {
  background: var(--gradient-aurora);
  border: none;
  border-radius: var(--radius-md);
  padding: 0.755rem 1.4rem;
  gap: 8px;
  color: var(--color-text-on-dark);
  font-family: var(--font-family);
  font-size: 0.875rem;
  line-height: 1.5;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  box-shadow: 0 3px 10px rgba(69, 74, 170, 0.35);
  animation: voice-pulse 2.2s ease-in-out infinite;
  transition: transform var(--transition-fast), box-shadow var(--transition-fast);
}

.voice-reader-btn--play:hover {
  transform: translateY(-1px) scale(1.04);
  box-shadow: 0 6px 18px rgba(69, 74, 170, 0.50);
  animation: none;
}

.voice-reader-btn--play .pi {
  font-size: 0.85rem;
}

.voice-reader-btn--limit {
  opacity: 0.65;
  cursor: not-allowed;
  animation: none;
  box-shadow: none;
}

.voice-reader-btn--limit:hover {
  transform: none;
  box-shadow: none;
}

.play-label {
  line-height: 1;
}

@keyframes voice-pulse {
  0%, 100% { box-shadow: 0 3px 10px rgba(69, 74, 170, 0.35); }
  50%       { box-shadow: 0 3px 18px rgba(229, 171, 114, 0.65); }
}

/* ── Aurora waveform bars (shown while playing) ── */
.voice-waveform {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 22px;
  padding: 0 4px;
  align-self: center;
}

.wave-bar {
  display: block;
  width: 3px;
  height: 20px;
  border-radius: 2px;
  background: linear-gradient(to top, var(--color-aurora-start), var(--color-aurora-end));
  transform-origin: bottom center;
  animation: wave-bounce 1.1s ease-in-out infinite;
}

.wave-bar:nth-child(1) { animation-delay: 0s;    }
.wave-bar:nth-child(2) { animation-delay: 0.22s; }
.wave-bar:nth-child(3) { animation-delay: 0.44s; }
.wave-bar:nth-child(4) { animation-delay: 0.22s; }
.wave-bar:nth-child(5) { animation-delay: 0s;    }

@keyframes wave-bounce {
  0%, 100% { transform: scaleY(0.25); opacity: 0.60; }
  50%       { transform: scaleY(1);   opacity: 1;    }
}

.voice-reader-error {
  font-size: 0.75rem;
  color: var(--color-error);
  margin-left: 8px;
}
</style>
